// Push Notification Service for TaxFilePro
// Handles FCM token management and notification sending

import { db, requestNotificationPermission, getFCMToken, onForegroundMessage, showLocalNotification, isVapidKeyConfigured } from './firebase';
import { doc, setDoc, getDoc, collection, getDocs, serverTimestamp } from 'firebase/firestore';

// Re-export isVapidKeyConfigured for easy checking
export { isVapidKeyConfigured };

// Format date from YYYY-MM-DD to MM/DD/YYYY
const formatDate = (dateString) => {
  if (!dateString) return 'TBD';
  const date = new Date(dateString + 'T00:00:00');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};

// Convert 24-hour time to 12-hour format
const formatTime = (time24) => {
  if (!time24) return 'TBD';
  const [hours, minutes] = time24.split(':');
  const hour = parseInt(hours);
  const period = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes} ${period}`;
};

// Store FCM token for a preparer
export const subscribePreparer = async (preparerId, companyId) => {
  try {
    // Check if VAPID key is configured first
    if (!isVapidKeyConfigured()) {
      console.log('❌ VAPID key not configured');
      return { 
        success: false, 
        error: 'VAPID_KEY_NOT_CONFIGURED',
        message: 'Push notifications are not configured yet. Please contact your administrator to set up the VAPID key in Firebase Console.'
      };
    }
    
    // Request notification permission
    const permissionGranted = await requestNotificationPermission();
    if (!permissionGranted) {
      console.log('Notification permission not granted');
      return { success: false, error: 'Permission denied' };
    }

    // Get FCM token
    const tokenResult = await getFCMToken();
    
    // Check if tokenResult is an error object
    if (tokenResult && typeof tokenResult === 'object' && tokenResult.error) {
      console.log('Could not get FCM token:', tokenResult.error);
      
      if (tokenResult.error === 'VAPID_KEY_NOT_CONFIGURED') {
        return { 
          success: false, 
          error: 'VAPID_KEY_NOT_CONFIGURED',
          message: 'Push notifications are not configured yet. Please contact your administrator to set up the VAPID key in Firebase Console.'
        };
      }
      
      return { success: false, error: tokenResult.error };
    }
    
    if (!tokenResult) {
      console.log('Could not get FCM token');
      return { success: false, error: 'Could not get notification token' };
    }

    const token = tokenResult;

    // Store token in Firestore
    const tokenRef = doc(db, 'fcmTokens', `${companyId}_${preparerId}`);
    await setDoc(tokenRef, {
      token,
      preparerId,
      companyId,
      platform: 'web',
      userAgent: navigator.userAgent,
      subscribedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      active: true
    }, { merge: true });

    console.log('✅ Preparer subscribed to notifications');
    return { success: true, token };
  } catch (error) {
    console.error('Error subscribing preparer:', error);
    return { success: false, error: error.message };
  }
};

// Unsubscribe preparer from notifications
export const unsubscribePreparer = async (preparerId, companyId) => {
  try {
    const tokenRef = doc(db, 'fcmTokens', `${companyId}_${preparerId}`);
    await setDoc(tokenRef, {
      active: false,
      unsubscribedAt: serverTimestamp()
    }, { merge: true });

    console.log('✅ Preparer unsubscribed from notifications');
    return { success: true };
  } catch (error) {
    console.error('Error unsubscribing preparer:', error);
    return { success: false, error: error.message };
  }
};

// Check if preparer is subscribed
export const isPreparerSubscribed = async (preparerId, companyId) => {
  try {
    const tokenRef = doc(db, 'fcmTokens', `${companyId}_${preparerId}`);
    const tokenDoc = await getDoc(tokenRef);
    
    if (tokenDoc.exists()) {
      const data = tokenDoc.data();
      return data.active === true && data.token;
    }
    return false;
  } catch (error) {
    console.error('Error checking subscription:', error);
    return false;
  }
};

// Get all FCM tokens for a company's preparers
export const getCompanyFCMTokens = async (companyId) => {
  try {
    const tokensRef = collection(db, 'fcmTokens');
    const snapshot = await getDocs(tokensRef);
    
    const tokens = [];
    snapshot.forEach((doc) => {
      const data = doc.data();
      if (data.companyId === companyId && data.active && data.token) {
        tokens.push({
          id: doc.id,
          ...data
        });
      }
    });
    
    return tokens;
  } catch (error) {
    console.error('Error getting company tokens:', error);
    return [];
  }
};

// Store notification in Firestore (for notification history)
export const storeNotification = async (notification) => {
  try {
    const notifRef = doc(collection(db, 'notifications'));
    await setDoc(notifRef, {
      ...notification,
      createdAt: serverTimestamp(),
      read: false
    });
    return { success: true, id: notifRef.id };
  } catch (error) {
    console.error('Error storing notification:', error);
    return { success: false, error: error.message };
  }
};

// Send notification to preparer when appointment is booked
// Note: For actual push notifications to work, you need a backend server
// This function stores the notification and shows local notification if app is open
export const notifyNewAppointment = async (appointment, preparerInfo, companyId) => {
  try {
    // Handle both field naming conventions (fullName/email or clientName/clientEmail)
    const clientName = appointment.fullName || appointment.clientName || 'Client';
    const clientEmail = appointment.email || appointment.clientEmail || '';
    const clientPhone = appointment.phoneNumber || appointment.clientPhone || '';
    const appointmentTime = appointment.time || appointment.timeSlots?.[0] || '';
    
    const title = '📅 New Appointment Booked';
    const body = `${clientName} booked for ${formatDate(appointment.date)} at ${formatTime(appointmentTime)}`;
    
    // Store notification in Firestore
    await storeNotification({
      type: 'new_appointment',
      title,
      body,
      preparerId: preparerInfo.id || preparerInfo.name,
      companyId,
      appointmentId: appointment.id,
      data: {
        clientName,
        clientEmail,
        clientPhone,
        date: appointment.date,
        time: appointmentTime,
        services: appointment.services || []
      }
    });

    // Show local notification if the app is in foreground
    showLocalNotification(title, body, { appointmentId: appointment.id });

    console.log('✅ Appointment notification sent');
    return { success: true };
  } catch (error) {
    console.error('Error sending appointment notification:', error);
    return { success: false, error: error.message };
  }
};

// Notify when appointment is confirmed
export const notifyAppointmentConfirmed = async (appointment, companyId) => {
  try {
    const title = '✅ Appointment Confirmed';
    const body = `Your appointment on ${formatDate(appointment.date)} at ${formatTime(appointment.timeSlots?.[0] || appointment.time)} has been confirmed`;
    
    await storeNotification({
      type: 'appointment_confirmed',
      title,
      body,
      companyId,
      appointmentId: appointment.id,
      clientEmail: appointment.clientEmail
    });

    return { success: true };
  } catch (error) {
    console.error('Error sending confirmation notification:', error);
    return { success: false, error: error.message };
  }
};

// Notify when appointment is cancelled
export const notifyAppointmentCancelled = async (appointment, companyId) => {
  try {
    const title = '❌ Appointment Cancelled';
    const body = `Appointment for ${appointment.clientName} on ${formatDate(appointment.date)} has been cancelled`;
    
    await storeNotification({
      type: 'appointment_cancelled',
      title,
      body,
      companyId,
      appointmentId: appointment.id
    });

    showLocalNotification(title, body);
    return { success: true };
  } catch (error) {
    console.error('Error sending cancellation notification:', error);
    return { success: false, error: error.message };
  }
};

// Setup foreground message listener
export const setupNotificationListener = (callback) => {
  return onForegroundMessage((payload) => {
    console.log('📬 Received foreground message:', payload);
    
    // Show browser notification
    const title = payload.notification?.title || 'New Notification';
    const body = payload.notification?.body || '';
    showLocalNotification(title, body, payload.data);
    
    // Call custom callback
    if (callback) {
      callback(payload);
    }
  });
};

// Check if push notifications are supported
export const isPushSupported = () => {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
};

// Get notification permission status
export const getNotificationPermission = () => {
  if (!('Notification' in window)) {
    return 'unsupported';
  }
  return Notification.permission; // 'granted', 'denied', or 'default'
};

// Send appointment reminder to preparer
export const notifyAppointmentReminder = async (appointment, preparerInfo, companyId, minutesBefore = 15) => {
  try {
    const clientName = appointment.fullName || appointment.clientName || 'Client';
    const appointmentTime = appointment.time || appointment.timeSlots?.[0] || '';
    
    const title = `⏰ Appointment in ${minutesBefore} minutes`;
    const body = `${clientName} - ${formatDate(appointment.date)} at ${formatTime(appointmentTime)}`;
    
    // Store notification in Firestore
    await storeNotification({
      type: 'appointment_reminder',
      title,
      body,
      preparerId: preparerInfo.id || preparerInfo.name,
      companyId,
      appointmentId: appointment.id,
      data: {
        clientName,
        date: appointment.date,
        time: appointmentTime,
        minutesBefore
      }
    });

    // Show local notification
    showLocalNotification(title, body, { 
      appointmentId: appointment.id,
      type: 'reminder'
    });

    console.log(`✅ Reminder sent: ${minutesBefore} minutes before appointment`);
    return { success: true };
  } catch (error) {
    console.error('Error sending reminder notification:', error);
    return { success: false, error: error.message };
  }
};

// Check and send reminders for upcoming appointments
export const checkAndSendReminders = async (appointments, preparerInfo, companyId) => {
  const now = new Date();
  const remindersSent = [];
  
  for (const apt of appointments) {
    if (!apt.confirmed) continue; // Only remind for confirmed appointments
    
    const aptDateTime = new Date(`${apt.date}T${apt.time || '00:00'}`);
    const diffMinutes = Math.floor((aptDateTime - now) / (1000 * 60));
    
    // Send reminder at 15 minutes, 30 minutes, or 1 hour before
    const reminderIntervals = [60, 30, 15];
    
    for (const interval of reminderIntervals) {
      if (diffMinutes > interval - 2 && diffMinutes <= interval + 2) {
        // Check if reminder already sent (stored in localStorage)
        const reminderKey = `reminder_${apt.id}_${interval}`;
        if (!localStorage.getItem(reminderKey)) {
          await notifyAppointmentReminder(apt, preparerInfo, companyId, interval);
          localStorage.setItem(reminderKey, 'sent');
          remindersSent.push({ appointment: apt, minutes: interval });
        }
      }
    }
  }
  
  return remindersSent;
};

// Clear old reminder flags (call this daily)
export const clearOldReminderFlags = () => {
  const today = new Date().toISOString().split('T')[0];
  const keys = Object.keys(localStorage).filter(k => k.startsWith('reminder_'));
  
  keys.forEach(key => {
    // Remove reminder flags older than today
    const storedDate = localStorage.getItem(key + '_date');
    if (storedDate && storedDate < today) {
      localStorage.removeItem(key);
      localStorage.removeItem(key + '_date');
    }
  });
};
